export type LegalTab = 'terms' | 'privacy' | 'refund';

export interface LegalSection {
  title: string;
  paragraphs?: string[];
  bullets?: string[];
}

export interface LegalDocument {
  key: LegalTab;
  title: string;
  subtitle: string;
  sections: LegalSection[];
}

export const legalDocuments: LegalDocument[] = [
  {
    key: 'terms',
    title: '服務條款',
    subtitle: '請仔細閱讀以下條款內容。',
    sections: [
      {
        title: '一、導言與締約背景',
        paragraphs: [
          '本《服務條款》係由 NexusGo Labs Limited 與使用本平台服務的用戶之間具有法律約束力的協議。',
          '一旦您訪問網站、註冊帳戶或使用任何服務，即表示您已閱讀並同意受本協議所有條款約束。如不同意，請立即停止使用。'
        ]
      },
      {
        title: '二、服務內容與非商業使用',
        paragraphs: [
          '平台主要提供前沿互聯網技術教育、企業數字化轉型諮詢、行業研究分析及專業技能培訓服務。'
        ],
        bullets: [
          '僅授予您個人及非商業目的的有限使用許可。',
          '不得複製、轉售、公開傳播課程視頻、講義、諮詢錄音等內容。',
          '不得對網站進行反向工程、反編譯或試圖獲取源碼。'
        ]
      },
      {
        title: '三、風險與免責聲明',
        bullets: [
          '平台內容僅供技術教育與行業研究參考，不構成投資、財務、交易或法律建議。',
          '互聯網前沿技術與新興商業模式具高波動性與不確定性，相關決策風險由您自行承擔。'
        ]
      },
      {
        title: '四、用戶行為規範',
        bullets: [
          '不得從事洗黑錢、恐怖融資、非法集資、詐騙、賭博等非法用途。',
          '不得發布誹謗、淫褻、暴力、仇恨或侵權內容。',
          '不得使用爬蟲、腳本或惡意程式攻擊網站與伺服器。',
          '不得盜用或共享帳戶以規避付費。'
        ]
      },
      {
        title: '五、知識產權與帳戶安全',
        bullets: [
          '平台內文字、圖像、音頻、視頻、程式與版面設計的知識產權歸 NexusGo Labs Limited 或其許可方所有。',
          '您須妥善保管帳戶密碼與登入資訊，因保管不善造成的損失由您自行承擔。',
          '若發現異常登入或違規交易，我們有權在不另行通知下凍結或註銷帳戶。'
        ]
      },
      {
        title: '六、責任限制與法律適用',
        bullets: [
          '對於不可抗力、網絡故障、黑客攻擊或政府管制造成的服務中斷或數據丟失，我們不承擔責任。',
          '在法律允許範圍內，我們對您的總賠償責任不超過索賠前 6 個月內您支付的服務費總額。',
          '本協議受香港特別行政區法律管轄，爭議應先協商，協商不成提交香港法院。'
        ]
      }
    ]
  },
  {
    key: 'privacy',
    title: '私隱政策',
    subtitle: '請仔細閱讀以下私隱說明。',
    sections: [
      {
        title: '特別提示',
        paragraphs: [
          '本政策對您使用產品或服務非常重要。若您不同意本政策中的任何內容，應立即停止使用我們的產品或服務。'
        ]
      },
      {
        title: '一、我們如何收集和使用您的個人資料',
        bullets: [
          '為完成帳戶註冊、課程購買、服務預約與身份驗證，我們會收集電子郵箱、登入名、暱稱、頭像與聯繫方式。',
          '為提升服務品質，我們可能收集職業與教育背景，以便匹配合適的導師與課程內容。',
          '在瀏覽網站時，系統會自動收集設備資訊、IP、瀏覽器類型、點擊與訪問時間等服務日誌。'
        ]
      },
      {
        title: '二、Cookie 與同類技術',
        bullets: [
          '我們會使用 Cookie 記住您的偏好設定、減少重複填表並改善登入體驗。',
          '您可在瀏覽器中調整 Cookie 設定，但拒絕後可能影響部分功能正常使用。'
        ]
      },
      {
        title: '三、共享、轉讓與跨境傳輸',
        bullets: [
          '僅在獲得明確同意或符合法律要求時共享資料。',
          '支付流程可能涉及 Stripe、PayPal 等支付機構，但我們不直接接觸您的完整信用卡號。',
          '由於公司運營與服務安排，您的資料可能被傳輸至香港以外地區處理。'
        ]
      },
      {
        title: '四、資料保護與您的權利',
        bullets: [
          '平台採用 SSL/TLS 加密、訪問控制與安全預警機制保護資料。',
          '您享有查閱、複製、更正、刪除與註銷帳戶等權利。',
          '依據香港《個人資料（私隱）條例》，我們將在收到請求後 40 天內回覆。'
        ]
      },
      {
        title: '五、未成年人與聯繫方式',
        bullets: [
          '服務主要面向成人，未成年人需在監護人陪同下閱讀並獲得同意。',
          '如有資料權利申請或私隱疑問，可聯繫：admin@nexusgo.ai。'
        ]
      }
    ]
  },
  {
    key: 'refund',
    title: '退款政策',
    subtitle: '請仔細閱讀以下退款規則。',
    sections: [
      {
        title: '一、退款原則概覽',
        bullets: [
          '購買前請確認產品大綱與需求相符。',
          '所有批准的退款將原路退回至原支付帳戶。',
          '退款核准後通常於 7 至 14 個工作日內處理。'
        ]
      },
      {
        title: '二、不同類型服務的退款規則',
        bullets: [
          '數碼內容與錄播課程原則上不退款；若購買後 7 天內、進度為 0%、未下載附件且因技術故障無法訪問，可申請全額退款。',
          '一對一輔導與預約諮詢在開始前 24 小時可全額退款；24 小時內取消則退 50%；開始後不退款。',
          '企業定製培訓與項目諮詢以雙方簽署的服務合同或 SOW 為準。'
        ]
      },
      {
        title: '三、不可退款情況',
        bullets: [
          '非官方渠道購買的產品或服務。',
          '存在共享帳戶、非法錄製課程、惡意攻擊平台等違反服務條款情況。',
          '以沒時間學、改變主意、主觀不適合等非產品品質問題為由提出的退款申請。',
          '已標示為特價或不可退款的促銷商品。'
        ]
      },
      {
        title: '四、如何申請退款',
        bullets: [
          '請以「退款申請 - [您的訂單號]」為標題寄送郵件至 admin@nexusgo.ai。',
          '郵件內請附上註冊郵箱、帳戶名稱、購買產品、購買日期、退款理由與相關證明。',
          '客服團隊將於 3 至 5 個工作日內完成審核與回覆。'
        ]
      }
    ]
  }
];
